import { Injectable } from '@angular/core';
import { RolesEnum } from '@irembo-andela/irembogov3-common';
import { KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { KeycloakProfile } from 'keycloak-js';
import {
  BehaviorSubject,
  from,
  interval,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userActiveRole: BehaviorSubject<RolesEnum> = new BehaviorSubject<RolesEnum>(
    RolesEnum._UNKNOWN_ROLE_
  );
  private userActiveRoleSubject = new BehaviorSubject<RolesEnum>(
    RolesEnum._UNKNOWN_ROLE_
  );
  public userActiveRole$ = this.userActiveRoleSubject.asObservable();
  private cancelSubscription$ = new Subject<void>();

  // Keep the session alive during long editing sessions: proactively refresh the
  // access token (which rotates the refresh token and resets the SSO idle timer)
  // so the user is not silently logged out when they finally hit an API.
  private readonly TOKEN_REFRESH_INTERVAL_MS = 60_000;
  private readonly TOKEN_MIN_VALIDITY_SECONDS = 90;

  constructor(private keyCloakService: KeycloakService) {
    this.redirectUserOnTokenExpired();
    this.startBackgroundTokenRefresh();
  }

  async isLoggedIn(): Promise<boolean> {
    return this.keyCloakService.isLoggedIn();
  }

  loadUserProfile(): Observable<KeycloakProfile> {
    return from(this.keyCloakService.loadUserProfile());
  }

  async loadUserIremboRoles(): Promise<RolesEnum[]> {
    const userIremboRoles: RolesEnum[] = [];

    if (!(await this.keyCloakService.isLoggedIn())) return userIremboRoles;

    const keycloakRolesList: string[] = this.keyCloakService.getUserRoles();

    Object.keys(RolesEnum).forEach((role: string) => {
      if (keycloakRolesList.includes(role)) {
        userIremboRoles.push(role as RolesEnum);
      }
    });

    if (
      userIremboRoles.length > 0 &&
      this.userActiveRole.getValue() === RolesEnum._UNKNOWN_ROLE_
    ) {
      this.userActiveRole.next(userIremboRoles[0]);
    }

    return userIremboRoles;
  }

  async loadRawUserRoles(): Promise<string[]> {
    if (!(await this.keyCloakService.isLoggedIn())) return [];

    return this.keyCloakService.getUserRoles();
  }

  /**
   * Observers when the token is expired or token refresh process fails
   * and redirects the user to login page
   */
  redirectUserOnTokenExpired() {
    this.keyCloakService.keycloakEvents$
      .pipe(takeUntil(this.cancelSubscription$))
      .subscribe(event => {
        if (event.type === KeycloakEventType.OnAuthRefreshError) {
          this.keyCloakService.login({
            prompt: 'login',
          });
        }
      });
  }

  /**
   * Proactively refresh the token in the background so a long editing session
   * (little/no API traffic) doesn't expire. Runs on a timer and also refreshes
   * immediately when Keycloak reports the token expired.
   */
  private startBackgroundTokenRefresh(): void {
    interval(this.TOKEN_REFRESH_INTERVAL_MS)
      .pipe(takeUntil(this.cancelSubscription$))
      .subscribe(() => this.refreshToken());

    this.keyCloakService.keycloakEvents$
      .pipe(takeUntil(this.cancelSubscription$))
      .subscribe(event => {
        if (event.type === KeycloakEventType.OnTokenExpired) {
          this.refreshToken();
        }
      });
  }

  /** Refresh the access token if it expires within the configured window. */
  private async refreshToken(): Promise<void> {
    try {
      if (!(await this.keyCloakService.isLoggedIn())) return;
      await this.keyCloakService.updateToken(this.TOKEN_MIN_VALIDITY_SECONDS);
    } catch {
      // A failed refresh surfaces as OnAuthRefreshError, which is already
      // handled in redirectUserOnTokenExpired() (redirects to login).
    }
  }

  /**
   * Ends the session of the user
   * and redirects the user to the login page
   */
  async logoutUser(): Promise<void> {
    await this.keyCloakService.logout();
    this.keyCloakService.clearToken();
  }

  // Get the first allowed role in the array and make it active role.
  async firstAllowedRole() {
    // Fetch and handle roles in ngOnInit
    return await this.checkAndReturnFirstAllowedRole([
      RolesEnum.ROLE_QA_REVIEWER,
      RolesEnum.ROLE_SERVICE_MANAGER,
      RolesEnum.ROLE_IREMBOGOV_ADMIN,
    ]);
  }

  // Check and return first in the allowed roles
  async checkAndReturnFirstAllowedRole(
    allowedRoles: RolesEnum[]
  ): Promise<RolesEnum | undefined> {
    const iremboRoles: RolesEnum[] = await this.loadUserIremboRoles();
    const firstAllowedRoleInIremboRoles = allowedRoles.find(role =>
      iremboRoles.includes(role)
    );
    return firstAllowedRoleInIremboRoles;
  }

  // Method to change role
  setUserRole(newRole: RolesEnum) {
    this.userActiveRoleSubject.next(newRole);
  }

  // Method to get the current role
  getUserRole(): RolesEnum {
    return this.userActiveRoleSubject.getValue();
  }
}
